import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { MozartSelectField } from '@pgforsta/mozart_ui';
import { MozartButton } from '@pgforsta/mozart_ui/lib/v4';
import { debounce } from 'lodash';
import Layout from '../Layout/Layout';
import FileInput from '../../components/FileInput/FileInput';
import UrlFinderModal from '../../components/UrlFinderModal/UrlFinderModal';
import { Card, CardHeader, CardContent } from '../../components/styled/Card';
import { Field, Label } from '../../components/styled/Field';
import TextArea from '../../components/styled/TextArea';
import { getSources } from '../../api/services/sources';
import { search } from '../../api/services/entities';
import { useAuth } from '../../context/auth';
import { useLists } from '../../context/lists';
import useAsync from '../../hooks/async';
import useUrlFinder from '../../hooks/url-finder';

const Sources = styled.div`
  color: #7b70f6;
  font-size: 14px;
`;

const SourcesLabel = styled.span`
  color: #3e3a83;
`;

const Source = styled.span``;

const Separator = styled.hr`
  border: none;
  border-top: 1px solid #d9d5f9;
  margin: 20px 0;
`;

const FormWrapper = styled.div`
  width: 50%;
  margin: 0 auto;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const initialFormData = {
  clientId: '',
  entity: '',
  entitiesIds: '',
};

const CrawlerConfig = () => {
  const history = useHistory();

  const [formData, setFormData] = useState(initialFormData);
  const sources = useAsync();
  const entities = useAsync();
  const urlFinder = useUrlFinder();
  const [resetEntitiesList, setResetEntitiesList] = useState(false);

  const { user } = useAuth();
  const { clients } = useLists();

  const { run: runSources } = sources;

  useEffect(() => {
    runSources(getSources());
  }, [runSources]);

  const mappedEntities = useMemo(
    () =>
      formData.entitiesIds
        .split(',')
        .map((entityId) => Number(entityId))
        .filter((entityId) => !isNaN(entityId) && entityId > 0),
    [formData.entitiesIds]
  );

  const renderActions = () => (
    <MozartButton outline onClick={() => history.push('/')}>
      Back to Dashboard
    </MozartButton>
  );

  const renderSources = () =>
    sources.status === 'resolved'
      ? sources.data.map((source, i) => (
          <Source key={source}>
            <span>{source}</span>
            {`${i < sources.data.length - 1 ? ', ' : '.'}`}
          </Source>
        ))
      : null;

  const getClientsData = () => {
    if (!clients.isResolved) {
      return [];
    }
    return clients.data.map((client) => ({
      value: client.id,
      text: client.name,
    }));
  };

  const getEntitiesData = () => {
    if (!entities.isResolved || resetEntitiesList) {
      return [];
    }
    return entities.data.map((entity) => ({
      value: entity.entityId,
      text: entity.npi ? `${entity.name} (${entity.npi})` : entity.name,
    }));
  };

  const handleUrlFinderModalClose = () => {
    setFormData(initialFormData);
    urlFinder.closeModal();
  };

  const handleEntityInput = debounce((input) => {
    if (input.length < 3) {
      return;
    }
    entities.run(search(input)).then(() => {
      setResetEntitiesList(false);
    });
  }, 750);

  const handleCsvUpload = (event) => {
    const { files } = event.target;
    if (files.length === 0) {
      return;
    }
    const data = new FormData();
    data.append('userEmailId', user.email);
    data.append('file', files[0]);
    urlFinder.trigger(data, true).then(() => {
      setResetEntitiesList(true);
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = { userEmailId: user.email };
    if (formData.clientId) {
      data.clientId = formData.clientId;
    } else {
      data.entityIds = formData.entity ? [formData.entity] : mappedEntities;
    }
    urlFinder.trigger(data).then(() => {
      setResetEntitiesList(true);
    });
  };

  return (
    <Layout title="Trigger URL Finder" actions={renderActions()}>
      <Card>
        <CardHeader>Client and Entity Selection</CardHeader>
        <CardContent>
          <Sources>
            <SourcesLabel>Sources:</SourcesLabel> {renderSources()}
          </Sources>

          <Separator />

          <FormWrapper>
            <form onSubmit={handleSubmit}>
              <Field>
                <Label>Client</Label>
                <MozartSelectField
                  defaultValue={formData.clientId}
                  data={getClientsData()}
                  onChange={(value) => {
                    setFormData((prevFormData) => ({
                      ...prevFormData,
                      clientId: Array.isArray(value) ? '' : value,
                    }));
                  }}
                  clearable
                  searchable
                  disabled={
                    Boolean(formData.entity) ||
                    Boolean(formData.entitiesIds.trim())
                  }
                />
              </Field>

              <Field>
                <Label>Provider / Location</Label>
                <MozartSelectField
                  defaultValue={formData.entity}
                  data={getEntitiesData()}
                  onInputChangeFunc={handleEntityInput}
                  onChange={(value) => {
                    setFormData((prevFormData) => ({
                      ...prevFormData,
                      entity: Array.isArray(value) ? '' : value,
                    }));
                  }}
                  clearable
                  searchable
                  disabled={
                    Boolean(formData.clientId) ||
                    Boolean(formData.entitiesIds.trim())
                  }
                />
              </Field>

              <Field>
                <Label>Entities</Label>
                <TextArea
                  rows={3}
                  value={formData.entitiesIds}
                  onChange={(event) => {
                    setFormData((prevFormData) => ({
                      ...prevFormData,
                      entitiesIds: event.target.value,
                    }));
                  }}
                  disabled={
                    Boolean(formData.clientId) || Boolean(formData.entity)
                  }
                />
              </Field>

              <Actions>
                <FileInput
                  id="upload-csv"
                  name="upload-csv"
                  label="Upload .CSV"
                  accept=".csv"
                  outline
                  onChange={handleCsvUpload}
                />

                <MozartButton
                  type="submit"
                  disabled={
                    (!Boolean(formData.clientId) &&
                      !Boolean(formData.entity) &&
                      mappedEntities.length === 0) ||
                    urlFinder.query.status === 'pending'
                  }
                >
                  Trigger
                </MozartButton>
              </Actions>
            </form>
          </FormWrapper>
        </CardContent>
      </Card>

      <UrlFinderModal
        show={urlFinder.showModal}
        status={urlFinder.query.status}
        onClose={handleUrlFinderModalClose}
        clientId={formData.clientId}
      />
    </Layout>
  );
};

export default CrawlerConfig;
